
import { useState } from 'react'
import { supabase } from '../supabase'
import { Link } from 'react-router-dom'
import { useBrand } from '../hooks/useBrand'
import { useTranslation } from '../i18n/LanguageContext'

const STATUS_COLORS = {
  received:     'bg-blue-100 text-blue-700',
  pending:      'bg-yellow-100 text-yellow-700',
  confirmed:    'bg-amber-100 text-amber-700',
  processing:   'bg-teal-100 text-teal-700',
  with_courier: 'bg-purple-100 text-purple-700',
  shipped:      'bg-green-100 text-green-700',
  closed:       'bg-gray-100 text-gray-600',
  return:       'bg-red-100 text-red-700',
}

const STATUS_LABELS = {
  received: 'Received', pending: 'Pending', confirmed: 'Confirmed',
  processing: 'Processing', with_courier: 'With Courier',
  shipped: 'Shipped', closed: 'Closed', return: 'Returned',
}

const COLORS = ['#6366F1','#EC4899','#F97316','#22C55E','#3B82F6','#8B5CF6','#EAB308','#14B8A6']

// Platform brand colors — matches CustomerOrderForm for a consistent PageConnect look
const NAVY = '#0B1F3A'
const TEAL = '#2DD4B0'

let pdfGenerating = false

function generateReceiptPDF(order, seller, brand) {
  if (pdfGenerating) return
  pdfGenerating = true
  import('jspdf').then(({ default: jsPDF }) => {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' })
    const pw = doc.internal.pageSize.getWidth()
    let y = 20
    const center = (text, yPos, size = 10) => { doc.setFontSize(size); doc.text(text, pw / 2, yPos, { align: 'center' }) }
    const line   = (yPos) => { doc.setDrawColor(220, 220, 220); doc.line(20, yPos, pw - 20, yPos) }
    const row    = (label, value, yPos) => { doc.setFontSize(9); doc.setFont('helvetica', 'normal'); doc.setTextColor(120, 120, 120); doc.text(label, 20, yPos); doc.setTextColor(40, 40, 40); doc.text(String(value), pw - 20, yPos, { align: 'right' }) }
    doc.setFont('helvetica', 'bold'); doc.setFontSize(18); doc.setTextColor(37, 99, 235)
    center(brand.name, y, 18); y += 6
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(120, 120, 120)
    center(brand.website, y, 9); y += 10; line(y); y += 8
    doc.setFont('helvetica', 'bold'); doc.setFontSize(14); doc.setTextColor(20, 20, 20)
    center(seller?.shop_name || 'Seller', y, 14); y += 6
    if (seller?.phone) { doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(100, 100, 100); center(seller.phone, y, 9); y += 5 }
    if (seller?.facebook_url) { const username = seller.facebook_url.split('/').filter(Boolean).pop(); doc.setFontSize(9); doc.setTextColor(37, 99, 235); center('facebook.com/' + username, y, 9); y += 5 }
    if (seller?.district || seller?.country) { doc.setFontSize(9); doc.setTextColor(120, 120, 120); center(seller.district || seller.country, y, 9); y += 5 }
    if (seller?.verification_status === 'verified') { doc.setFontSize(9); doc.setTextColor(22, 163, 74); center('Verified Seller', y, 9); y += 5 }
    y += 4; line(y); y += 8
    doc.setFont('helvetica', 'bold'); doc.setFontSize(11); doc.setTextColor(80, 80, 80)
    center('RECEIPT', y, 11); y += 6
    doc.setFontSize(12); doc.setTextColor(37, 99, 235); center(order.order_code, y, 12); y += 5
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(120, 120, 120)
    center(new Date(order.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }), y, 9); y += 10; line(y); y += 8
    doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(80, 80, 80)
    doc.text('PRODUCT', 20, y); y += 6
    doc.setFont('helvetica', 'normal'); doc.setFontSize(10); doc.setTextColor(20, 20, 20)
    doc.text(order.product_name || order.product_code || 'Product', 20, y); y += 5
    if (order.variant_name) { doc.setFontSize(9); doc.setTextColor(120, 120, 120); doc.text('Variant: ' + order.variant_name, 20, y); y += 5 }
    const unitTotal = Number(order.unit_price || 0) * Number(order.quantity || 1)
    row('Qty: ' + order.quantity + ' x ' + Number(order.unit_price || 0).toLocaleString(), unitTotal.toLocaleString(), y); y += 5
    row('Delivery charge', Number(order.delivery_charge || 0).toLocaleString(), y); y += 6; line(y); y += 6
    doc.setFont('helvetica', 'bold'); doc.setFontSize(11); doc.setTextColor(20, 20, 20)
    doc.text('Total', 20, y); doc.setTextColor(37, 99, 235)
    doc.text(Number(order.total_amount || 0).toLocaleString(), pw - 20, y, { align: 'right' }); y += 10
    line(y); y += 8
    doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(80, 80, 80); doc.text('PAYMENT', 20, y); y += 6
    row('Method', order.payment_method || 'N/A', y); y += 5
    row('Status', order.payment_status || 'N/A', y); y += 5
    if (Number(order.amount_paid || 0) > 0) { doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(22, 163, 74); doc.text('Paid', 20, y); doc.text(Number(order.amount_paid).toLocaleString(), pw - 20, y, { align: 'right' }); y += 5 }
    const balance = Number(order.total_amount || 0) - Number(order.amount_paid || 0)
    if (balance > 0) { doc.setTextColor(217, 119, 6); doc.text('Balance due', 20, y); doc.setFont('helvetica', 'bold'); doc.text(balance.toLocaleString(), pw - 20, y, { align: 'right' }); y += 5 }
    y += 3; line(y); y += 8
    doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(80, 80, 80); doc.text('CUSTOMER', 20, y); y += 6
    doc.setFont('helvetica', 'normal'); doc.setFontSize(10); doc.setTextColor(20, 20, 20)
    doc.text(order.customers?.name || 'Customer', 20, y); y += 5
    if (order.customers?.phone) { doc.setFontSize(9); doc.setTextColor(120, 120, 120); doc.text(order.customers.phone, 20, y); y += 5 }
    y += 3; line(y); y += 8
    doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(80, 80, 80); doc.text('DELIVERY', 20, y); y += 6
    row('Courier', order.courier || 'N/A', y); y += 5
    if (order.tracking_number) { row('Tracking no.', order.tracking_number, y); y += 5 }
    if (order.expected_delivery) { row('Expected', order.expected_delivery, y); y += 5 }
    y += 5; line(y); y += 8
    doc.setFont('helvetica', 'bold'); doc.setFontSize(11); doc.setTextColor(37, 99, 235)
    center('Thank you for shopping!', y, 11); y += 6
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(120, 120, 120)
    center('with ' + (seller?.shop_name || 'the seller'), y, 9); y += 5
    center('For queries contact the seller directly.', y, 9); y += 10
    doc.setFontSize(7); doc.setTextColor(160, 160, 160)
    const disclaimer = 'This receipt is generated by ' + brand.name + ' on behalf of the seller. All product, delivery and payment matters are solely between the buyer and seller. ' + brand.name + ' is a technology platform only and holds no liability for any transaction, delivery or dispute.'
    const dLines = doc.splitTextToSize(disclaimer, pw - 40)
    doc.text(dLines, 20, y); y += dLines.length * 4 + 5; line(y); y += 6
    doc.setFontSize(8); doc.setTextColor(37, 99, 235)
    center('Powered by ' + brand.name + ' · ' + brand.website, y, 8)
    doc.save('receipt-' + order.order_code + '.pdf')
    pdfGenerating = false
  }).catch(() => { pdfGenerating = false })
}

function Receipt({ order, seller, brand, onClose, t }) {
  const balance = Number(order.total_amount || 0) - Number(order.amount_paid || 0)
  const shopUsername = seller?.facebook_username || seller?.facebook_url?.split('/').filter(Boolean).pop() || ''

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end sm:items-center justify-center"
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white w-full sm:max-w-sm sm:rounded-2xl rounded-t-2xl shadow-xl flex flex-col" style={{ maxHeight: '90vh' }}>
        <div className="px-4 py-3 flex items-center justify-between rounded-t-2xl flex-shrink-0" style={{ background: NAVY }}>
          <div className="text-white text-sm font-semibold">{t('order_history.e_receipt')}</div>
          <div className="flex gap-2">
            <button onClick={() => generateReceiptPDF(order, seller, brand)}
              className="bg-white bg-opacity-20 text-white text-xs px-3 py-1.5 rounded-lg font-medium">
              {t('order_history.download_pdf')}
            </button>
            <button onClick={onClose} className="bg-white bg-opacity-20 text-white text-xs px-3 py-1.5 rounded-lg">
              {t('order_history.close')}
            </button>
          </div>
        </div>

        <div className="overflow-y-auto flex-1">
          <div className="p-5">
            <div className="text-center border-b border-gray-100 pb-3 mb-4">
              {brand.logo_url
                ? <img src={brand.logo_url} alt={brand.name} className="h-7 object-contain mx-auto mb-1" />
                : <div className="text-sm font-bold" style={{ color: NAVY }}>{brand.name}</div>}
              <div className="text-xs text-gray-400">{brand.website}</div>
            </div>

            <div className="text-center border-b border-gray-100 pb-4 mb-4">
              {seller?.logo_url
                ? <img src={seller.logo_url} alt="logo" className="w-14 h-14 rounded-2xl object-cover mx-auto mb-2 border border-gray-100" />
                : <div className="w-14 h-14 rounded-2xl bg-gray-50 flex items-center justify-center mx-auto mb-2">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#0B1F3A" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l1-5h16l1 5M4 9v10a1 1 0 001 1h14a1 1 0 001-1V9M4 9h16M9 21v-6h6v6" /></svg>
                </div>}
              <div className="text-base font-bold text-gray-800">{seller?.shop_name}</div>
              {seller?.verification_status === 'verified' && (
                <div className="text-xs text-green-600 font-medium mt-0.5">{t('order_history.verified_seller')}</div>
              )}
              {seller?.phone && <div className="text-xs text-gray-500 mt-1">{seller.phone}</div>}
              {seller?.facebook_url && <div className="text-xs mt-0.5" style={{ color: TEAL }}>facebook.com/{shopUsername}</div>}
              {(seller?.district || seller?.country) && <div className="text-xs text-gray-400 mt-0.5">{seller.district || seller.country}</div>}
            </div>

            <div className="text-center mb-4">
              <div className="text-xs font-bold text-gray-400 tracking-widest mb-1">RECEIPT</div>
              <div className="text-sm font-bold font-mono" style={{ color: TEAL }}>{order.order_code}</div>
              <div className="text-xs text-gray-400">{new Date(order.created_at).toLocaleDateString('en-BD', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
            </div>

            <div className="border-t border-gray-100 pt-4 mb-4">
              <div className="flex justify-between items-start mb-2">
                <div className="flex-1 mr-3">
                  <div className="text-sm font-semibold text-gray-800">{order.product_name || order.product_code}</div>
                  {order.variant_name && <div className="text-xs text-gray-400 mt-0.5">{t('order_history.variant')} {order.variant_name}</div>}
                  <div className="text-xs text-gray-400 mt-0.5">{order.quantity} x {Number(order.unit_price || 0).toLocaleString()}</div>
                </div>
                <div className="text-sm font-bold text-gray-800">{(Number(order.unit_price || 0) * Number(order.quantity || 1)).toLocaleString()}</div>
              </div>
              <div className="flex justify-between text-xs text-gray-400 mb-3">
                <span>{t('order_history.delivery_charge')}</span>
                <span>{Number(order.delivery_charge || 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center border-t border-gray-100 pt-3">
                <span className="text-sm font-bold text-gray-800">{t('order_history.total')}</span>
                <span className="text-base font-bold" style={{ color: TEAL }}>{Number(order.total_amount || 0).toLocaleString()}</span>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-3 mb-4">
              <div className="text-xs font-semibold text-gray-600 mb-2">{t('order_history.payment')}</div>
              <div className="flex justify-between text-xs mb-1">
                <span className="text-gray-400">{t('order_history.method')}</span>
                <span className="text-gray-700 font-medium">{order.payment_method || 'N/A'}</span>
              </div>
              <div className="flex justify-between text-xs mb-1">
                <span className="text-gray-400">{t('order_history.status_label')}</span>
                <span className="text-gray-700 font-medium">{order.payment_status || 'N/A'}</span>
              </div>
              {Number(order.amount_paid || 0) > 0 && (
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-green-600">{t('order_history.paid')}</span>
                  <span className="text-green-600 font-semibold">{Number(order.amount_paid).toLocaleString()}</span>
                </div>
              )}
              {balance > 0 && (
                <div className="flex justify-between text-xs border-t border-gray-200 pt-2 mt-1">
                  <span className="text-amber-600 font-medium">{t('order_history.balance_due')}</span>
                  <span className="text-amber-600 font-bold">{balance.toLocaleString()}</span>
                </div>
              )}
            </div>

            <div className="border-t border-gray-100 pt-4 mb-4">
              <div className="text-xs font-semibold text-gray-600 mb-2">{t('order_history.customer')}</div>
              <div className="text-sm font-medium text-gray-800">{order.customers?.name || 'N/A'}</div>
              {order.customers?.phone && <div className="text-xs text-gray-400 mt-0.5">{order.customers.phone}</div>}
            </div>

            <div className="border-t border-gray-100 pt-4 mb-4">
              <div className="text-xs font-semibold text-gray-600 mb-2">{t('order_history.delivery')}</div>
              <div className="flex justify-between text-xs mb-1">
                <span className="text-gray-400">{t('order_history.courier')}</span>
                <span className="text-gray-700 font-medium">{order.courier || 'N/A'}</span>
              </div>
              {order.tracking_number && (
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-gray-400">{t('order_history.tracking')}</span>
                  <span className="text-gray-700 font-mono">{order.tracking_number}</span>
                </div>
              )}
              {order.expected_delivery && (
                <div className="flex justify-between text-xs">
                  <span className="text-gray-400">{t('order_history.expected')}</span>
                  <span className="text-gray-700">{order.expected_delivery}</span>
                </div>
              )}
            </div>

            <div className="text-center border-t border-gray-100 pt-4 mb-4">
              <div className="text-sm font-semibold mb-1" style={{ color: NAVY }}>{t('order_history.thank_you')}</div>
              <div className="text-xs text-gray-400 mb-1">{t('order_history.with_seller')} {seller?.shop_name}</div>
              <div className="text-xs text-gray-400">{t('order_history.contact_seller')}</div>
            </div>

            <div className="bg-gray-50 rounded-xl p-3 mb-3">
              <div className="text-xs text-gray-400 leading-relaxed">
                {brand.name}{t('order_history.receipt_disclaimer')}{brand.name}{t('order_history.receipt_disclaimer2')}
              </div>
            </div>

            <div className="text-center mb-2">
              <div className="text-xs text-gray-400 mb-1">{t('order_history.powered_by')}</div>
              {brand.logo_url
                ? <img src={brand.logo_url} alt={brand.name} className="h-6 object-contain mx-auto mb-1" />
                : <div className="text-sm font-bold" style={{ color: NAVY }}>{brand.name}</div>}
              <div className="text-xs text-gray-400">{brand.website}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function OrderHistory() {
  const { brand } = useBrand()
  const { t } = useTranslation()

  const [phone, setPhone]               = useState('')
  const [orders, setOrders]             = useState([])
  const [sellers, setSellers]           = useState({})
  const [loading, setLoading]           = useState(false)
  const [searched, setSearched]         = useState(false)
  const [error, setError]               = useState('')
  const [receipt, setReceipt]           = useState(null)
  const [filterSeller, setFilterSeller] = useState('all')
  const [filterStatus, setFilterStatus] = useState('all')

  const handleSearch = async () => {
    if (!phone || phone.length < 6) { setError(t('order_history.error_phone')); return }
    setLoading(true); setError(''); setSearched(false); setOrders([])
    const { data: customers } = await supabase.from('customers').select('id, seller_id').eq('phone', phone)
    if (!customers || customers.length === 0) { setSearched(true); setLoading(false); return }
    const customerIds = customers.map(c => c.id)
    const sellerIds   = [...new Set(customers.map(c => c.seller_id))]
    const { data: ordersData } = await supabase.from('orders')
      .select('*, customers(name, phone)').in('customer_id', customerIds).order('created_at', { ascending: false })
    const { data: sellersData } = await supabase.from('sellers')
      .select('id, shop_name, facebook_url, facebook_username, phone, logo_url, district, country, verification_status').in('id', sellerIds)
    const sellerMap = {}; const customerSellerMap = {}
    customers.forEach(c => { customerSellerMap[c.id] = c.seller_id })
    sellersData?.forEach(s => { sellerMap[s.id] = s })
    const ordersWithSeller = (ordersData || []).map(o => ({ ...o, seller: sellerMap[customerSellerMap[o.customer_id]] }))
    setSellers(sellerMap); setOrders(ordersWithSeller); setSearched(true); setLoading(false)
  }

  const filteredOrders = orders.filter(o => {
    const matchSeller = filterSeller === 'all' || o.seller?.id === filterSeller
    const matchStatus = filterStatus === 'all' || o.status === filterStatus
    return matchSeller && matchStatus
  })

  const totalSpent  = orders.filter(o => o.status !== 'return').reduce((sum, o) => sum + Number(o.total_amount || 0), 0)
  const totalOrders = orders.length
  const avgOrder    = totalOrders > 0 ? totalSpent / totalOrders : 0

  const spendBySeller = Object.values(sellers).map(seller => {
    const sellerOrders = orders.filter(o => o.seller?.id === seller.id && o.status !== 'return')
    return { name: seller.shop_name, id: seller.id, spent: sellerOrders.reduce((sum, o) => sum + Number(o.total_amount || 0), 0), count: sellerOrders.length }
  }).sort((a, b) => b.spent - a.spent)

  const favSeller = spendBySeller[0]

  const fmt = (n) => {
    if (n >= 100000) return Math.round(n / 1000) + 'k'
    return Math.round(n).toLocaleString()
  }

  return (
    <div className="min-h-screen bg-gray-50">

      <div className="px-4 py-4 sticky top-0 z-40" style={{ background: NAVY }}>
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div>
            {brand.logo_url
              ? <img src={brand.logo_url} alt={brand.name} className="h-7 object-contain mb-0.5" />
              : <div className="text-white font-bold text-lg">{brand.name}</div>}
            <div className="text-xs" style={{ color: TEAL }}>{t('order_history.title')}</div>
          </div>
          <Link to="/track" className="text-xs hover:text-white" style={{ color: '#8FA3BD' }}>
            {t('order_history.track_orders')}
          </Link>
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-4">

        {/* Search card */}
        <div className="bg-white rounded-2xl p-5 mb-4 shadow-sm border border-gray-100 pc-fade-up">
          <div className="text-base font-bold text-gray-800 mb-1">{t('order_history.your_history')}</div>
          <div className="text-xs text-gray-400 mb-4">{t('order_history.enter_phone')}</div>
          <div className="flex gap-2">
            <input type="tel" value={phone}
              onChange={e => setPhone(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              placeholder={t('order_history.phone_placeholder')}
              className="flex-1 bg-gray-100 border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-teal-400" />
            <button onClick={handleSearch} disabled={loading}
              className="text-white px-5 py-3 rounded-xl text-sm font-semibold disabled:opacity-50 whitespace-nowrap"
              style={{ background: NAVY }}>
              {loading ? '...' : t('order_history.search')}
            </button>
          </div>
          {error && <div className="text-xs text-red-500 mt-2">{error}</div>}
        </div>

        {/* No results */}
        {searched && orders.length === 0 && (
          <div className="bg-white rounded-2xl p-6 text-center mb-4 border border-gray-100">
            <svg className="mx-auto mb-3" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <div className="text-sm font-semibold text-gray-700 mb-1">{t('order_history.no_orders_title')}</div>
            <div className="text-xs text-gray-400">{t('order_history.no_orders_desc')} {phone}</div>
          </div>
        )}

        {/* Results */}
        {searched && orders.length > 0 && (
          <>
            {/* Stats grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              {[
                { label: t('order_history.total_spent'),  value: fmt(totalSpent),             color: 'text-blue-600',   bg: 'bg-blue-50' },
                { label: t('order_history.total_orders'), value: totalOrders,                  color: 'text-purple-600', bg: 'bg-purple-50' },
                { label: t('order_history.avg_order'),    value: fmt(avgOrder),               color: 'text-amber-600',  bg: 'bg-amber-50' },
                { label: t('order_history.sellers'),      value: Object.keys(sellers).length, color: 'text-green-600',  bg: 'bg-green-50' },
              ].map(({ label, value, color, bg }) => (
                <div key={label} className={bg + ' rounded-2xl p-4 pc-fade-up pc-card-hover'}>
                  <div className={'text-xl font-bold ' + color}>{value}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{label}</div>
                </div>
              ))}
            </div>

            {/* Favourite seller */}
            {favSeller && (
              <div className="rounded-2xl p-4 mb-4 text-white pc-fade-up" style={{ background: NAVY }}>
                <div className="text-xs mb-1" style={{ color: '#8FA3BD' }}>{t('order_history.spending_insight')}</div>
                <div className="text-sm font-semibold">
                  {t('order_history.favourite_seller')}{' '}
                  <span style={{ color: TEAL }}>{favSeller.name}</span>
                </div>
                <div className="text-xs mt-0.5" style={{ color: '#8FA3BD' }}>
                  {favSeller.count} {t('order_history.orders')} · {fmt(favSeller.spent)} {t('order_history.total_spent').toLowerCase()}
                </div>
              </div>
            )}

            {/* Spend by seller bar chart */}
            {spendBySeller.length > 0 && (
              <div className="bg-white rounded-2xl p-4 mb-4 border border-gray-100 shadow-sm pc-fade-up">
                <div className="text-sm font-semibold text-gray-800 mb-3">{t('order_history.spending_by_seller')}</div>
                <div className="space-y-3">
                  {spendBySeller.map((s, i) => (
                    <div key={s.id}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: COLORS[i % COLORS.length] }} />
                          <span className="text-xs text-gray-700 font-medium truncate" style={{ maxWidth: '120px' }}>{s.name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-400">{s.count} {t('order_history.orders')}</span>
                          <span className="text-xs font-bold text-gray-800">{fmt(s.spent)}</span>
                        </div>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-1.5">
                        <div className="h-1.5 rounded-full" style={{ width: totalSpent > 0 ? ((s.spent / totalSpent) * 100) + '%' : '0%', background: COLORS[i % COLORS.length] }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Filters */}
            <div className="flex gap-2 mb-3 overflow-x-auto pb-1">
              <select value={filterSeller} onChange={e => setFilterSeller(e.target.value)}
                className="bg-white border border-gray-200 rounded-lg px-3 py-2 text-xs outline-none flex-shrink-0">
                <option value="all">{t('order_history.all_sellers')}</option>
                {Object.values(sellers).map(s => <option key={s.id} value={s.id}>{s.shop_name}</option>)}
              </select>
              <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
                className="bg-white border border-gray-200 rounded-lg px-3 py-2 text-xs outline-none flex-shrink-0">
                <option value="all">{t('order_history.all_status')}</option>
                <option value="closed">{t('order_history.delivered')}</option>
                <option value="return">{t('order_history.returned')}</option>
                <option value="shipped">{t('order_history.shipped')}</option>
                <option value="with_courier">{t('order_history.with_courier')}</option>
              </select>
            </div>

            {/* Order cards */}
            <div className="space-y-3 mb-4">
              {filteredOrders.map(order => (
                <div key={order.id} className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm pc-fade-up pc-card-hover">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="font-mono text-sm font-bold" style={{ color: TEAL }}>{order.order_code}</div>
                      <div className="text-xs text-gray-400 mt-0.5">
                        {order.seller?.shop_name} · {new Date(order.created_at).toLocaleDateString('en-BD', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </div>
                    </div>
                    <span className={'text-xs px-2.5 py-1 rounded-full font-medium flex-shrink-0 ml-2 ' + (STATUS_COLORS[order.status] || 'bg-gray-100 text-gray-600')}>
                      {STATUS_LABELS[order.status] || order.status}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0 mr-2">
                      <div className="text-xs text-gray-700 font-medium truncate">{order.product_name || order.product_code}</div>
                      {order.variant_name && <div className="text-xs text-gray-400">{order.variant_name}</div>}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <div className="text-sm font-bold text-gray-800">{fmt(Number(order.total_amount || 0))}</div>
                      <button onClick={() => setReceipt({ order, seller: order.seller })}
                        className="bg-gray-50 border border-gray-200 text-gray-500 text-xs px-2 py-1.5 rounded-lg hover:bg-gray-100 whitespace-nowrap">
                        {t('order_history.receipt')}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-xs text-gray-400 text-center mb-4">
              {filteredOrders.length} {t('order_history.of')} {orders.length} {t('order_history.orders_shown')}
            </div>
          </>
        )}

        {/* Disclaimer */}
        <div className="bg-red-50 border border-red-100 rounded-2xl p-4 mb-4">
          <div className="text-xs font-bold text-red-700 mb-2">{t('order_history.important_title')}</div>
          <div className="space-y-1.5">
            {[
              t('order_history.disclaimer_1'),
              brand.name + t('order_history.disclaimer_4'),
              t('order_history.disclaimer_3'),
              brand.name + t('order_history.disclaimer_4'),
            ].map((text, i) => (
              <div key={i} className="flex items-start gap-2">
                <span className="text-red-400 flex-shrink-0">•</span>
                <span className="text-xs text-red-600 leading-relaxed">{text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mb-2">
          {brand.logo_url
            ? <img src={brand.logo_url} alt={brand.name} className="h-6 object-contain mx-auto mb-1" />
            : <div className="text-sm font-bold" style={{ color: NAVY }}>{brand.name}</div>}
          <div className="text-xs text-gray-400">{brand.website} · {brand.email}</div>
        </div>
        <div className="text-xs text-gray-400 text-center leading-relaxed px-2 mb-6">
          {brand.name}{t('order_history.platform_note')}
        </div>

      </div>

      {receipt && (
        <Receipt order={receipt.order} seller={receipt.seller} brand={brand} onClose={() => setReceipt(null)} t={t} />
      )}

      <style>{`
        @keyframes pc-fade-up { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        .pc-fade-up { animation: pc-fade-up 0.4s ease-out both; }
        .pc-card-hover { transition: transform 0.15s ease, box-shadow 0.15s ease; }
        .pc-card-hover:hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(11,31,58,0.08); }
      `}</style>

    </div>
  )
}

export default OrderHistory